// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import {
  DefaultApi,
  DisclosedContract,
  EventFormat,
  Filters,
  JsContractEntry,
  JsContractEntryOneOf,
  TransactionFormatTransactionShapeEnum,
} from '@digitalasset/json-api-ts-client';
import hash from 'object-hash';
import { v4 as uuidv4 } from 'uuid';
import { Amulet, LockedAmulet } from '@daml.js/splice-amulet/lib/Splice/Amulet';
import { AllocationRequest } from '@daml.js/splice-api-token-allocation-request/lib/Splice/Api/Token/AllocationRequestV1';
import { Allocation } from '@daml.js/splice-api-token-allocation/lib/Splice/Api/Token/AllocationV1';
import { Holding as TokenStandardHolding } from '@daml.js/splice-api-token-holding/lib/Splice/Api/Token/HoldingV1';
import { TransferInstruction } from '@daml.js/splice-api-token-transfer-instruction/lib/Splice/Api/Token/TransferInstructionV1';
import {
  CollateralAgreement,
  CollateralAgreementChangeRequest,
  CollateralAgreementRequest,
  ExecutedCollateralTransfer,
  InstructedCollateral,
} from '@daml.js/utility-collateral-app-v1/lib/Utility/Collateral/App/Model/Collateral';
import { OperatorConfiguration as CollateralOperatorConfiguration } from '@daml.js/utility-collateral-app-v1/lib/Utility/Collateral/App/Model/Configuration/Operator';
import { CollateralState } from '@daml.js/utility-collateral-app-v1/lib/Utility/Collateral/App/Model/State/module';
import {
  UserService as CollateralUserService,
  UserServiceRequest as CollateralUserServiceRequest,
} from '@daml.js/utility-collateral-app-v1/lib/Utility/Collateral/App/Service/User';
import {
  CollateralAgreement as CollateralAgreementV2,
  CollateralAgreement_SettlementObligation,
  CollateralAgreementRequest as CollateralAgreementRequestV2,
  CollateralAgreementChangeRequest as CollateralAgreementChangeRequestV2,
  SettlementInstruction,
  ExecutedCollateralTransfer as ExecutedCollateralTransferV2,
  FailedCollateralTransfer,
} from '@daml.js/utility-collateral-app-v2/lib/Utility/Collateral/App/Model/Collateral/module';
import { OperatorConfiguration as CollateralOperatorConfigurationV2 } from '@daml.js/utility-collateral-app-v2/lib/Utility/Collateral/App/Model/Configuration/Operator';
import { CollateralState as CollateralStateV2 } from '@daml.js/utility-collateral-app-v2/lib/Utility/Collateral/App/Model/State/module';
import {
  UserService as CollateralUserServiceV2,
  UserServiceRequest as CollateralUserServiceRequestV2,
} from '@daml.js/utility-collateral-app-v2/lib/Utility/Collateral/App/Service/User';
import { CommercialAgreement } from '@daml.js/utility-commercials-v0/lib/Utility/Commercials/V0/Model/CommercialAgreement';
import { CommercialAgreementOffer } from '@daml.js/utility-commercials-v0/lib/Utility/Commercials/V0/Model/Offer';
import { CredentialOffer } from '@daml.js/utility-credential-app-v0/lib/Utility/Credential/App/V0/Model/Offer';
import { UserService } from '@daml.js/utility-credential-app-v0/lib/Utility/Credential/App/V0/Service/User';
import { Credential } from '@daml.js/utility-credential-v0/lib/Utility/Credential/V0/Credential';
import { OperatorConfiguration } from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Configuration/Operator';
import { ProviderConfiguration } from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Configuration/Provider';
import {
  BurnRequest as BurnRequestTwoStep,
  ExecutedBurn as ExecutedBurnTwoStep,
  RejectedBurn as RejectedBurnTwoStep,
} from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Model/Burn';
import {
  ExecutedMint as ExecutedMintTwoStep,
  MintRequest as MintRequestTwoStep,
  RejectedMint as RejectedMintTwoStep,
} from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Model/Mint';
import { TransferPreapproval as RegistryTransferPreapproval } from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Model/TransferPreapproval';
import { AllocationFactory } from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Service/AllocationFactory';
import {
  HolderService,
  HolderServiceRequest,
} from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Service/Holder';
import {
  ProviderService,
  ProviderServiceRequest,
} from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Service/Provider';
import {
  RegistrarService,
  RegistrarServiceRequest,
} from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Service/Registrar';
import { Holding } from '@daml.js/utility-registry-holding-v0/lib/Utility/Registry/Holding/V0/Holding';
import { AppRewardConfiguration } from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Configuration/AppReward';
import { InstrumentConfiguration } from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Configuration/Instrument';
import {
  AcceptedBurn,
  BurnOffer,
  BurnRequest,
  ExecutedBurn,
  FailedBurn,
  RejectedBurn,
} from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Holding/Burn';
import {
  AcceptedLock,
  ExecutedLock,
  FailedLock,
  LockOffer,
  LockRequest,
  RejectedLock,
} from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Holding/Lock';
import {
  AcceptedMint,
  ExecutedMint,
  FailedMint,
  MintOffer,
  MintRequest,
  RejectedMint,
} from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Holding/Mint';
import {
  AcceptedTransfer,
  ExecutedTransfer,
  FailedTransfer,
  RejectedTransfer,
  TransferOffer,
  TransferRequest,
} from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Holding/Transfer';
import {
  AcceptedUnlock,
  ExecutedUnlock,
  FailedUnlock,
  RejectedUnlock,
  UnlockOffer,
  UnlockRequest,
} from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Holding/Unlock';
import { TransferRule } from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Rule/Transfer';
import {
  Party,
  Choice,
  ContractId,
  DisclosedContract as DamlDisclosedContract,
  InterfaceCompanion,
  List,
  Template,
} from '@daml/types';
import { ACSDatabase, ACSDatabaseValue } from '../config/config';
import { PackageName } from '../config/package';
import { ACSCache, ACSCacheIndexedDB, ACSCacheLRU, ACSHelper } from './ACS';
import { ACSCacheOPFS } from './ACS/ACSCacheOPFS';
import InterfaceContract from './InterfaceContract';
import TemplateContract from './TemplateContract';
import { WSSupport } from './WSSupport';

type CreateEvent<T extends object, K = unknown, I extends string = string> = {
  templateId: I;
  contractId: ContractId<T>;
  signatories: List<Party>;
  observers: List<Party>;
  key: K;
  payload: T;
};

type CommandMeta = {
  workflowId?: string;
  disclosedContracts?: DamlDisclosedContract[];
  domainId?: string;
};

type SubmitterArgs =
  | {
      actAs?: Party[];
      readAs?: Party[];
      submitter?: never;
    }
  | {
      actAs?: never;
      readAs?: never;
      submitter?: Party;
    };

export type LedgerCreateArgs<T extends object> = {
  argument: T;
  meta?: CommandMeta;
} & SubmitterArgs;

export type LedgerExerciseArgs<T extends object, C> = {
  cid: ContractId<T>;
  argument: C;
  meta?: CommandMeta;
} & SubmitterArgs;

const getACSCache = (useCache: boolean, database: ACSDatabaseValue): ACSCache | null => {
  if (!useCache) {
    console.log('Cache disabled...');
    return null;
  }

  if (ACSCacheOPFS.canUse() && database === ACSDatabase.OPFS) {
    console.log('Instantiating OPFS cache...');
    return new ACSCacheOPFS();
  }

  if (ACSCacheIndexedDB.canUse() && database === ACSDatabase.IndexedDB) {
    console.log('Instantiating IndexedDB cache...');
    return new ACSCacheIndexedDB();
  }

  if (ACSCacheLRU.canUse() && database === ACSDatabase.LRU) {
    console.log('Instantiating LRU cache...');
    return new ACSCacheLRU();
  }

  console.log('Cache disabled...');
  return null;
};

// Uses the JSON API (via @daml/ledger) to connect to the ledger.
export default class LedgerApiClientV2 {
  private apiInstance: DefaultApi;

  private userId: string;

  private defaultSubmitter: string;

  private deduplicationDurationInSeconds: number;

  // needs to be marked with # in order to avoid serialization (LedgerApiClientV2 is serialized when used as key)
  #acsHelper: ACSHelper;

  constructor(
    apiInstance: DefaultApi,
    userId: string,
    defaultParty: string,
    deduplicationDurationInSeconds: number,
    wsSupport: WSSupport,
    database: ACSDatabaseValue,
  ) {
    this.apiInstance = apiInstance;

    this.userId = userId;

    this.deduplicationDurationInSeconds = deduplicationDurationInSeconds;

    this.defaultSubmitter = defaultParty;

    this.#acsHelper = new ACSHelper(
      this.apiInstance,
      wsSupport,
      // When user has disabled WebSocket supports we want to always refetch entire ACS instead of
      // trying to do incremental updates.
      getACSCache(wsSupport.enabled(), database),
    );
  }

  async getCacheSize(): Promise<number> {
    return this.#acsHelper.getCacheSize();
  }

  async clearAllActiveContractsStateCache(): Promise<void> {
    await this.#acsHelper.clearAllCache();
  }

  async getPreferredPackageVersion(packageName: PackageName, operator: string) {
    return this.apiInstance
      .getV2InteractiveSubmissionPreferredPackageVersion(packageName, [
        operator,
        this.defaultSubmitter,
      ])
      .catch((error) => {
        console.warn(
          `No preferred package version retrieved for "${packageName}" (operator: ${operator}):`,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          typeof error === 'object' && 'message' in error ? error.message : error,
        );
        return {};
      });
  }

  setDefaultSubmitter(party: string): void {
    this.defaultSubmitter = party;
  }

  api() {
    return this.apiInstance;
  }

  async queryTransferInstructions(
    offset: number,
  ): Promise<InterfaceContract<TransferInstruction>[]> {
    return this.queryInterface(offset, TransferInstruction);
  }

  async queryCollateralAgreementChangeRequests(
    offset: number,
  ): Promise<TemplateContract<CollateralAgreementChangeRequest>[]> {
    const res = this.query(offset, CollateralAgreementChangeRequest);
    return res;
  }

  async queryBurnRequestsTwoStep(offset: number): Promise<TemplateContract<BurnRequestTwoStep>[]> {
    const res = this.query(offset, BurnRequestTwoStep);
    return res;
  }

  async queryExecutedBurnsTwoStep(
    offset: number,
  ): Promise<TemplateContract<ExecutedBurnTwoStep>[]> {
    const res = this.query(offset, ExecutedBurnTwoStep);
    return res;
  }

  async queryRejectedBurnsTwoStep(
    offset: number,
  ): Promise<TemplateContract<RejectedBurnTwoStep>[]> {
    const res = this.query(offset, RejectedBurnTwoStep);
    return res;
  }

  async queryMintRequestsTwoStep(offset: number): Promise<TemplateContract<MintRequestTwoStep>[]> {
    const res = this.query(offset, MintRequestTwoStep);
    return res;
  }

  async queryExecutedMintsTwoStep(
    offset: number,
  ): Promise<TemplateContract<ExecutedMintTwoStep>[]> {
    const res = this.query(offset, ExecutedMintTwoStep);
    return res;
  }

  async queryRejectedMintsTwoStep(
    offset: number,
  ): Promise<TemplateContract<RejectedMintTwoStep>[]> {
    const res = this.query(offset, RejectedMintTwoStep);
    return res;
  }

  async queryCredentialUserServices(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<UserService>[]> {
    const svcs = await this.query(offset, UserService);
    return svcs.filter((r) => {
      const { payload } = r;
      return payload.operator === operator;
    });
  }

  async queryCredentials(offset: number): Promise<TemplateContract<Credential>[]> {
    return this.query(offset, Credential);
  }

  async queryCredentialOffers(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<CredentialOffer>[]> {
    const svcs = await this.query(offset, CredentialOffer);
    return svcs.filter((r) => {
      const { payload } = r;
      return payload.operator === operator;
    });
  }

  async queryRegistryOperatorConfiguration(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<OperatorConfiguration> | null> {
    const configs = await this.query(offset, OperatorConfiguration);
    return (
      configs.find((r) => {
        const { payload } = r;
        return payload.operator === operator;
      }) || null
    );
  }

  async queryRegistryOperatorConfigurations(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<OperatorConfiguration>[]> {
    const configs = await this.query(offset, OperatorConfiguration);
    return configs.filter((r) => {
      const { payload } = r;
      return payload.operator === operator;
    });
  }

  async queryRegistryProviderConfiguration(
    offset: number,
    operator: string,
    provider: string,
  ): Promise<TemplateContract<ProviderConfiguration> | null> {
    const configs = await this.query(offset, ProviderConfiguration);
    return (
      configs.find((r) => {
        const { payload } = r;
        return payload.operator === operator && payload.provider === provider;
      }) || null
    );
  }

  async queryRegistryProviderConfigurations(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<ProviderConfiguration>[]> {
    const configs = await this.query(offset, ProviderConfiguration);
    return configs.filter((r) => {
      const { payload } = r;
      return payload.operator === operator;
    });
  }

  async queryRegistryInstrumentConfigurations(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<InstrumentConfiguration>[]> {
    const configs = await this.query(offset, InstrumentConfiguration);
    return configs.filter((r) => {
      const { payload } = r;
      return payload.operator === operator;
    });
  }

  async queryRegistryProviderServiceRequests(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<ProviderServiceRequest>[]> {
    const reqs = await this.query(offset, ProviderServiceRequest);
    return reqs.filter((r) => {
      const { payload } = r;
      return payload.operator === operator;
    });
  }

  async queryRegistryProviderServices(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<ProviderService>[]> {
    const reqs = await this.query(offset, ProviderService);
    return reqs.filter((r) => {
      const { payload } = r;
      return payload.operator === operator;
    });
  }

  async queryRegistryProviderService(
    offset: number,
    operator: string,
    party: string,
  ): Promise<TemplateContract<ProviderService> | null> {
    const svcs = await this.query(offset, ProviderService);
    return (
      svcs.find((r) => {
        const { payload } = r;
        return payload.operator === operator && payload.provider === party;
      }) || null
    );
  }

  async queryRegistryRegistrarServiceRequests(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<RegistrarServiceRequest>[]> {
    const reqs = await this.query(offset, RegistrarServiceRequest);
    return reqs.filter((r) => {
      const { payload } = r;
      return payload.operator === operator;
    });
  }

  async queryRegistryRegistrarServices(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<RegistrarService>[]> {
    const reqs = await this.query(offset, RegistrarService);
    return reqs.filter((r) => {
      const { payload } = r;
      return payload.operator === operator;
    });
  }

  async queryRegistryRegistrarService(
    offset: number,
    operator: string,
    party: string,
  ): Promise<TemplateContract<RegistrarService> | null> {
    const svcs = await this.query(offset, RegistrarService);
    return (
      svcs.find((r) => {
        const { payload } = r;
        return payload.operator === operator && payload.registrar === party;
      }) || null
    );
  }

  async queryRegistryHolderServiceRequests(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<HolderServiceRequest>[]> {
    const reqs = await this.query(offset, HolderServiceRequest);
    return reqs.filter((r) => {
      const { payload } = r;
      return payload.operator === operator;
    });
  }

  async queryRegistryHolderServices(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<HolderService>[]> {
    const reqs = await this.query(offset, HolderService);
    return reqs.filter((r) => {
      const { payload } = r;
      return payload.operator === operator;
    });
  }

  async queryRegistryHolderService(
    offset: number,
    operator: string,
    party: string,
  ): Promise<TemplateContract<HolderService> | null> {
    const svcs = await this.query(offset, HolderService);
    return (
      svcs.find((r) => {
        const { payload } = r;
        return payload.operator === operator && payload.holder === party;
      }) || null
    );
  }

  async queryHoldings(offset: number, operator: string): Promise<TemplateContract<Holding>[]> {
    const res = await this.query(offset, Holding);
    return res.filter((h) => {
      const { payload } = h;
      return (
        payload.operator === operator &&
        (payload.owner === this.defaultSubmitter ||
          payload.registrar === this.defaultSubmitter ||
          payload.provider === this.defaultSubmitter ||
          payload.operator === this.defaultSubmitter)
      );
    });
  }

  async queryAppRewardConfiguration(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<AppRewardConfiguration>[]> {
    const configs = await this.query(offset, AppRewardConfiguration);
    return configs.filter((r) => {
      const { payload } = r;
      return payload.operator === operator;
    });
  }

  async queryInstrumentConfigurations(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<InstrumentConfiguration>[]> {
    const res = await this.query(offset, InstrumentConfiguration);
    return res.filter((r) => {
      const { payload } = r;
      return payload.operator === operator;
    });
  }

  async queryRegistryTransferPreapproval(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<RegistryTransferPreapproval>[]> {
    const res = await this.query(offset, RegistryTransferPreapproval);
    return res.filter((r) => {
      const { payload } = r;
      return payload.operator === operator;
    });
  }

  async queryMintRequests(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<MintRequest>[]> {
    const res = await this.query(offset, MintRequest);
    return res.filter((r) => {
      const { payload } = r;
      return payload.mint.operator === operator;
    });
  }

  async queryMintOffers(offset: number, operator: string): Promise<TemplateContract<MintOffer>[]> {
    const res = await this.query(offset, MintOffer);
    return res.filter((r) => {
      const { payload } = r;
      return payload.mint.operator === operator;
    });
  }

  async queryAcceptedMints(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<AcceptedMint>[]> {
    const res = await this.query(offset, AcceptedMint);
    return res.filter((r) => {
      const { payload } = r;
      return payload.mint.operator === operator;
    });
  }

  async queryRejectedMints(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<RejectedMint>[]> {
    const res = await this.query(offset, RejectedMint);
    return res.filter((r) => {
      const { payload } = r;
      return payload.mint.operator === operator;
    });
  }

  async queryExecutedMints(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<ExecutedMint>[]> {
    const res = await this.query(offset, ExecutedMint);
    return res.filter((r) => {
      const { payload } = r;
      return payload.mint.operator === operator;
    });
  }

  async queryFailedMints(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<FailedMint>[]> {
    const res = await this.query(offset, FailedMint);
    return res.filter((r) => {
      const { payload } = r;
      return payload.mint.operator === operator;
    });
  }

  async queryBurnRequests(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<BurnRequest>[]> {
    const res = await this.query(offset, BurnRequest);
    return res.filter((r) => {
      const { payload } = r;
      return payload.burn.operator === operator;
    });
  }

  async queryBurnOffers(offset: number, operator: string): Promise<TemplateContract<BurnOffer>[]> {
    const res = await this.query(offset, BurnOffer);
    return res.filter((r) => {
      const { payload } = r;
      return payload.burn.operator === operator;
    });
  }

  async queryAcceptedBurns(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<AcceptedBurn>[]> {
    const res = await this.query(offset, AcceptedBurn);
    return res.filter((r) => {
      const { payload } = r;
      return payload.burn.operator === operator;
    });
  }

  async queryRejectedBurns(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<RejectedBurn>[]> {
    const res = await this.query(offset, RejectedBurn);
    return res.filter((r) => {
      const { payload } = r;
      return payload.burn.operator === operator;
    });
  }

  async queryExecutedBurns(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<ExecutedBurn>[]> {
    const res = await this.query(offset, ExecutedBurn);
    return res.filter((r) => {
      const { payload } = r;
      return payload.burn.operator === operator;
    });
  }

  async queryFailedBurns(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<FailedBurn>[]> {
    const res = await this.query(offset, FailedBurn);
    return res.filter((r) => {
      const { payload } = r;
      return payload.burn.operator === operator;
    });
  }

  async queryTransferRequests(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<TransferRequest>[]> {
    const res = await this.query(offset, TransferRequest);
    return res.filter((r) => {
      const { payload } = r;
      return payload.transfer.operator === operator;
    });
  }

  async queryTransferOffers(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<TransferOffer>[]> {
    const res = await this.query(offset, TransferOffer);
    return res.filter((r) => {
      const { payload } = r;
      return payload.transfer.operator === operator;
    });
  }

  async queryAcceptedTransfers(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<AcceptedTransfer>[]> {
    const res = await this.query(offset, AcceptedTransfer);
    return res.filter((r) => {
      const { payload } = r;
      return payload.transfer.operator === operator;
    });
  }

  async queryRejectedTransfers(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<RejectedTransfer>[]> {
    const res = await this.query(offset, RejectedTransfer);
    return res.filter((r) => {
      const { payload } = r;
      return payload.transfer.operator === operator;
    });
  }

  async queryExecutedTransfers(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<ExecutedTransfer>[]> {
    const res = await this.query(offset, ExecutedTransfer);
    return res.filter((r) => {
      const { payload } = r;
      return payload.transfer.operator === operator;
    });
  }

  async queryFailedTransfers(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<FailedTransfer>[]> {
    const res = await this.query(offset, FailedTransfer);
    return res.filter((r) => {
      const { payload } = r;
      return payload.transfer.operator === operator;
    });
  }

  async queryLockRequests(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<LockRequest>[]> {
    const res = await this.query(offset, LockRequest);
    return res.filter((r) => {
      const { payload } = r;
      return payload.lock.operator === operator;
    });
  }

  async queryLockOffers(offset: number, operator: string): Promise<TemplateContract<LockOffer>[]> {
    const res = await this.query(offset, LockOffer);
    return res.filter((r) => {
      const { payload } = r;
      return payload.lock.operator === operator;
    });
  }

  async queryAcceptedLocks(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<AcceptedLock>[]> {
    const res = await this.query(offset, AcceptedLock);
    return res.filter((r) => {
      const { payload } = r;
      return payload.lock.operator === operator;
    });
  }

  async queryRejectedLocks(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<RejectedLock>[]> {
    const res = await this.query(offset, RejectedLock);
    return res.filter((r) => {
      const { payload } = r;
      return payload.lock.operator === operator;
    });
  }

  async queryExecutedLocks(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<ExecutedLock>[]> {
    const res = await this.query(offset, ExecutedLock);
    return res.filter((r) => {
      const { payload } = r;
      return payload.lock.operator === operator;
    });
  }

  async queryFailedLocks(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<FailedLock>[]> {
    const res = await this.query(offset, FailedLock);
    return res.filter((r) => {
      const { payload } = r;
      return payload.lock.operator === operator;
    });
  }

  async queryUnlockRequests(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<UnlockRequest>[]> {
    const res = await this.query(offset, UnlockRequest);
    return res.filter((r) => {
      const { payload } = r;
      return payload.unlock.operator === operator;
    });
  }

  async queryUnlockOffers(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<UnlockOffer>[]> {
    const res = await this.query(offset, UnlockOffer);
    return res.filter((r) => {
      const { payload } = r;
      return payload.unlock.operator === operator;
    });
  }

  async queryAcceptedUnlocks(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<AcceptedUnlock>[]> {
    const res = await this.query(offset, AcceptedUnlock);
    return res.filter((r) => {
      const { payload } = r;
      return payload.unlock.operator === operator;
    });
  }

  async queryRejectedUnlocks(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<RejectedUnlock>[]> {
    const res = await this.query(offset, RejectedUnlock);
    return res.filter((r) => {
      const { payload } = r;
      return payload.unlock.operator === operator;
    });
  }

  async queryExecutedUnlocks(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<ExecutedUnlock>[]> {
    const res = await this.query(offset, ExecutedUnlock);
    return res.filter((r) => {
      const { payload } = r;
      return payload.unlock.operator === operator;
    });
  }

  async queryFailedUnlocks(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<FailedUnlock>[]> {
    const res = await this.query(offset, FailedUnlock);
    return res.filter((r) => {
      const { payload } = r;
      return payload.unlock.operator === operator;
    });
  }

  async queryCoins(offset: number, party: string): Promise<TemplateContract<Amulet>[]> {
    const res = await this.query(offset, Amulet);
    return res.filter((r) => r.payload.owner === party);
  }

  async queryLockedCoins(offset: number, party: string): Promise<TemplateContract<LockedAmulet>[]> {
    const res = await this.query(offset, LockedAmulet);
    return res.filter((r) => r.payload.amulet.owner === party);
  }

  // Collateral

  async queryCollateralOperatorConfiguration(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<CollateralOperatorConfiguration> | null> {
    const configs = await this.query(offset, CollateralOperatorConfiguration);
    return configs.find((r) => r.payload.operator === operator) || null;
  }

  async queryCollateralOperatorConfigurationV2(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<CollateralOperatorConfigurationV2> | null> {
    const configs = await this.query(offset, CollateralOperatorConfigurationV2);
    return configs.find((r) => r.payload.operator === operator) || null;
  }

  async queryCollateralState(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<CollateralState>[] | null> {
    const collateralStates = await this.query(offset, CollateralState);
    return collateralStates.filter((s) => s.payload.operator === operator);
  }

  async queryCollateralStateV2(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<CollateralStateV2>[] | null> {
    const collateralStates = await this.query(offset, CollateralStateV2);
    return collateralStates.filter((s) => s.payload.operator === operator);
  }

  async queryCollateralOperatorConfigurations(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<CollateralOperatorConfiguration>[] | null> {
    const configs = await this.query(offset, CollateralOperatorConfiguration);
    return configs.filter((r) => r.payload.operator === operator);
  }

  async queryCollateralOperatorConfigurationsV2(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<CollateralOperatorConfigurationV2>[] | null> {
    const configs = await this.query(offset, CollateralOperatorConfigurationV2);
    return configs.filter((r) => r.payload.operator === operator);
  }

  async queryCollateralUserServiceRequests(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<CollateralUserServiceRequest>[]> {
    const reqs = await this.query(offset, CollateralUserServiceRequest);
    return reqs.filter((r) => r.payload.operator === operator);
  }

  async queryCollateralUserServiceRequestsV2(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<CollateralUserServiceRequestV2>[]> {
    const reqs = await this.query(offset, CollateralUserServiceRequestV2);
    return reqs.filter((r) => r.payload.operator === operator);
  }

  async queryCollateralUserService(
    offset: number,
    operator: string,
    party: string,
  ): Promise<TemplateContract<CollateralUserService> | null> {
    const svcs = await this.query(offset, CollateralUserService);
    return svcs.find((r) => r.payload.operator === operator && r.payload.user === party) || null;
  }

  async queryCollateralUserServiceV2(
    offset: number,
    operator: string,
    party: string,
  ): Promise<TemplateContract<CollateralUserServiceV2> | null> {
    const svcs = await this.query(offset, CollateralUserServiceV2);
    return svcs.find((r) => r.payload.operator === operator && r.payload.user === party) || null;
  }

  async queryCollateralUserServices(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<CollateralUserService>[]> {
    const reqs = await this.query(offset, CollateralUserService);
    return reqs.filter((r) => r.payload.operator === operator);
  }

  async queryCollateralUserServicesV2(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<CollateralUserServiceV2>[]> {
    const reqs = await this.query(offset, CollateralUserServiceV2);
    return reqs.filter((r) => r.payload.operator === operator);
  }

  async queryCollateralAgreementRequests(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<CollateralAgreementRequest>[]> {
    const res = await this.query(offset, CollateralAgreementRequest);
    return res.filter((r) => r.payload.operator === operator);
  }

  async queryCollateralAgreements(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<CollateralAgreement>[]> {
    const res = await this.query(offset, CollateralAgreement);
    return res.filter((r) => r.payload.operator === operator);
  }

  async queryCollateralAgreementsV2(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<CollateralAgreementV2>[]> {
    const res = await this.query(offset, CollateralAgreementV2);
    return res.filter((r) => r.payload.operator === operator);
  }

  async queryCollateralAgreementSettlementObligations(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<CollateralAgreement_SettlementObligation>[]> {
    const res = await this.query(offset, CollateralAgreement_SettlementObligation);
    return res.filter((r) => r.payload.operator === operator);
  }

  async queryCollateralAgreementRequestsV2(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<CollateralAgreementRequestV2>[]> {
    const res = await this.query(offset, CollateralAgreementRequestV2);
    return res.filter((r) => r.payload.operator === operator);
  }

  async queryCollateralAgreementChangeRequestsV2(
    offset: number,
  ): Promise<TemplateContract<CollateralAgreementChangeRequestV2>[]> {
    const res = await this.query(offset, CollateralAgreementChangeRequestV2);
    return res;
  }

  async queryExecutedCollateralTransferV2(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<ExecutedCollateralTransferV2>[]> {
    const res = await this.query(offset, ExecutedCollateralTransferV2);
    return res.filter((r) => r.payload.operator === operator);
  }

  async querySettlementInstructions(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<SettlementInstruction>[]> {
    const res = await this.query(offset, SettlementInstruction);
    return res.filter((r) => r.payload.operator === operator);
  }

  async queryFailedCollateralTransfersV2(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<FailedCollateralTransfer>[]> {
    const res = await this.query(offset, FailedCollateralTransfer);
    return res.filter((r) => r.payload.operator === operator);
  }

  async queryInstructedCollateral(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<InstructedCollateral>[]> {
    const res = await this.query(offset, InstructedCollateral);
    return res.filter((r) => r.payload.operator === operator);
  }

  async queryExecutedCollateralTransfer(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<ExecutedCollateralTransfer>[]> {
    const res = await this.query(offset, ExecutedCollateralTransfer);
    return res.filter((r) => r.payload.operator === operator);
  }

  // Commercial Agreements
  async queryCommercialAgreements(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<CommercialAgreement>[]> {
    const res = await this.query(offset, CommercialAgreement);
    return res.filter((c) => c.payload.operator === operator);
  }

  async queryCommercialAgreementOffers(
    offset: number,
    operator: string,
  ): Promise<TemplateContract<CommercialAgreementOffer>[]> {
    return (await this.query(offset, CommercialAgreementOffer)).filter(
      (offer) => offer.payload.operator === operator,
    );
  }

  async queryTokenStandardHoldings(
    offset: number,
  ): Promise<InterfaceContract<TokenStandardHolding>[]> {
    return this.queryInterface(offset, TokenStandardHolding);
  }

  async queryAllocationFactory(
    offset: number,
    operator: string,
    registrar: string,
  ): Promise<TemplateContract<AllocationFactory>[]> {
    const res = await this.query(offset, AllocationFactory);
    return res.filter((c) => c.payload.operator === operator && c.payload.registrar === registrar);
  }

  async queryAllocations(offset: number): Promise<InterfaceContract<Allocation>[]> {
    return this.queryInterface(offset, Allocation);
  }

  async queryAllocationRequestInterface(
    offset: number,
  ): Promise<InterfaceContract<AllocationRequest>[]> {
    return this.queryInterface(offset, AllocationRequest);
  }

  async queryTransferRules(
    offset: number,
    operator: string,
    registrar: string,
  ): Promise<TemplateContract<TransferRule>[]> {
    const res = await this.query(offset, TransferRule);
    return res.filter((c) => c.payload.operator === operator && c.payload.registrar === registrar);
  }

  async getLedgerEnd(): Promise<number> {
    return this.apiInstance.getV2StateLedgerEnd().then((r) => r.offset);
  }

  async create<T extends object, K, I extends string>(
    template: Template<T, K, I>,
    argument: T,
    meta?: CommandMeta,
    actAs: string[] = this.defaultSubmitter ? [this.defaultSubmitter] : [],
    readAs: string[] = [],
  ): Promise<CreateEvent<T, K, I>> {
    const commandId = hash({
      templateId: template.templateId,
      argument,
      actAs,
      readAs,
    });

    const command = {
      CreateCommand: {
        templateId: template.templateId,
        createArguments: template.encode(argument),
      },
    };

    const commands = {
      commands: {
        commands: [command],
        workflowId: meta?.workflowId || '',
        userId: this.userId,
        commandId,
        deduplicationPeriod: this.getDeduplicationDuration(),
        actAs,
        readAs,
        submissionId: uuidv4(),
        disclosedContracts: LedgerApiClientV2.toDisclosedContracts(meta),
        domainId: meta?.domainId || '',
        packageIdSelectionPreference: [],
      },
      transactionFormat: LedgerApiClientV2.getTransactionFormat(
        actAs,
        TransactionFormatTransactionShapeEnum.TRANSACTION_SHAPE_ACS_DELTA,
      ),
    };

    const response = await this.apiInstance.postV2CommandsSubmitAndWaitForTransaction(commands);

    const event = response.transaction.events?.at(0);
    if (event && 'CreatedEvent' in event) {
      const createdEvent = event.CreatedEvent;
      return {
        templateId: template.templateId,
        contractId: createdEvent.contractId as ContractId<T>,
        signatories: createdEvent.signatories || [],
        observers: createdEvent.observers || [],
        key: createdEvent.contractKey as K,
        payload: template.decoder.runWithException(createdEvent.createArgument),
      };
    }
    return Promise.reject(new Error('CreatedEvent is expected from the response'));
  }

  async exercise<T extends object, C, R, K>(
    choice: Choice<T, C, R, K>,
    cid: ContractId<T>,
    argument: C,
    meta?: CommandMeta,
    actAs: string[] = this.defaultSubmitter ? [this.defaultSubmitter] : [],
    readAs: string[] = [],
  ): Promise<R> {
    const commandId = hash({
      contractId: cid,
      templateId: choice.template().templateId,
      choiceName: choice.choiceName,
      argument,
      actAs,
      readAs,
    });

    const command = {
      ExerciseCommand: {
        templateId: choice.template().templateId,
        contractId: cid,
        choice: choice.choiceName,
        choiceArgument: choice.argumentEncode(argument),
      },
    };

    const commands = {
      commands: {
        commands: [command],
        workflowId: meta?.workflowId || '',
        userId: this.userId,
        commandId,
        deduplicationPeriod: this.getDeduplicationDuration(),
        actAs,
        readAs,
        submissionId: uuidv4(),
        disclosedContracts: LedgerApiClientV2.toDisclosedContracts(meta),
        domainId: meta?.domainId || '',
        packageIdSelectionPreference: [],
      },
      transactionFormat: LedgerApiClientV2.getTransactionFormat(
        actAs,
        TransactionFormatTransactionShapeEnum.TRANSACTION_SHAPE_LEDGER_EFFECTS,
      ),
    };

    const response = await this.apiInstance.postV2CommandsSubmitAndWaitForTransaction(commands);

    const event = response.transaction.events?.at(0);
    if (event && 'ExercisedEvent' in event) {
      const exercisedEvent = event.ExercisedEvent;
      if ('exerciseResult' in exercisedEvent) {
        return choice.resultDecoder.runWithException(exercisedEvent.exerciseResult);
      }
    }

    return Promise.reject(new Error('ExercisedEvent is expected from the response'));
  }

  async query<T extends object, K, I extends string>(
    offset: number,
    template: Template<T, K, I>,
    party: string = this.defaultSubmitter,
  ): Promise<TemplateContract<T>[]> {
    return this.activeContractsForTemplate(offset, party, template.templateId).then((responses) => {
      return responses
        .map((resp) => resp.contractEntry)
        .filter(LedgerApiClientV2.isJsActiveContract)
        .map((activeContract) =>
          TemplateContract.fromJsonAPIV2(template, activeContract.JsActiveContract.createdEvent),
        );
    });
  }

  private async queryInterface<T extends object, K, I extends string>(
    offset: number,
    interfaceCompanion: InterfaceCompanion<T, K, I>,
    party: string = this.defaultSubmitter,
  ): Promise<InterfaceContract<T>[]> {
    return this.activeContractsForInterface(offset, party, interfaceCompanion.templateId).then(
      (responses) => {
        return responses
          .map((resp) => resp.contractEntry)
          .filter(LedgerApiClientV2.isJsActiveContract)
          .map((activeContract) =>
            InterfaceContract.fromJsonAPIV2(
              interfaceCompanion,
              activeContract.JsActiveContract.createdEvent,
            ),
          );
      },
    );
  }

  private static isJsActiveContract = (
    contract: JsContractEntry,
  ): contract is JsContractEntryOneOf => {
    return 'JsActiveContract' in contract;
  };

  async prepareCreate<T extends object, K, I extends string>(
    template: Template<T, K, I>,
    argument: T,
    meta?: CommandMeta,
    actAs: string[] = this.defaultSubmitter ? [this.defaultSubmitter] : [],
    readAs: string[] = [],
  ) {
    const commandId = hash({
      templateId: template.templateId,
      argument,
      actAs,
      readAs,
    });

    const command = {
      CreateCommand: {
        templateId: template.templateId,
        createArguments: template.encode(argument),
      },
    };

    const domains = await this.apiInstance.getV2StateConnectedSynchronizers(actAs[0]);
    const domain = domains.connectedSynchronizers ? domains.connectedSynchronizers[0] : undefined;

    const request = {
      userId: this.userId,
      commandId,
      commands: [command],
      actAs,
      readAs,
      disclosedContracts: LedgerApiClientV2.toDisclosedContracts(meta),
      domainId: domain?.synchronizerId || '',
      synchronizerId: domain?.synchronizerId || '',
      packageIdSelectionPreference: [],
      verboseHashing: true,
    };

    return this.apiInstance.postV2InteractiveSubmissionPrepare(request);
  }

  async prepareExercise<T extends object, C, R, K>(
    choice: Choice<T, C, R, K>,
    cid: ContractId<T>,
    argument: C,
    meta?: CommandMeta,
    actAs: string[] = this.defaultSubmitter ? [this.defaultSubmitter] : [],
    readAs: string[] = [],
  ) {
    const commandId = hash({
      contractId: cid,
      templateId: choice.template().templateId,
      choiceName: choice.choiceName,
      argument,
      actAs,
      readAs,
    });

    const command = {
      ExerciseCommand: {
        templateId: choice.template().templateId,
        contractId: cid,
        choice: choice.choiceName,
        choiceArgument: choice.argumentEncode(argument),
      },
    };

    const domains = await this.apiInstance.getV2StateConnectedSynchronizers(actAs[0]);
    const domain = domains.connectedSynchronizers ? domains.connectedSynchronizers[0] : undefined;

    const request = {
      userId: this.userId,
      commandId,
      commands: [command],
      actAs,
      readAs,
      disclosedContracts: LedgerApiClientV2.toDisclosedContracts(meta, domain?.synchronizerId),
      domainId: domain?.synchronizerId || '',
      synchronizerId: domain?.synchronizerId || '',
      packageIdSelectionPreference: [],
      verboseHashing: true,
    };

    return this.apiInstance.postV2InteractiveSubmissionPrepare(request);
  }

  async activeContractsForTemplate(offset: number, partyFilter: string, templateId: string) {
    return this.#acsHelper.activeContractsForTemplate(offset, partyFilter, templateId);
  }

  async activeContractsForInterface(offset: number, partyFilter: string, interfaceId: string) {
    return this.#acsHelper.activeContractsForInterface(offset, partyFilter, interfaceId);
  }

  async activeContracts(filters: Filters, offset: number, partyFilter: string | undefined) {
    const eventFormat: EventFormat = partyFilter
      ? {
          filtersByParty: {
            [partyFilter]: filters,
          },
          verbose: false,
        }
      : {
          filtersByParty: {},
          filtersForAnyParty: filters,
          verbose: false,
        };

    return this.apiInstance.postV2StateActiveContracts({
      activeAtOffset: offset,
      verbose: false,
      eventFormat,
    });
  }

  private static toDisclosedContracts(meta?: CommandMeta, domainId?: string): DisclosedContract[] {
    return (
      meta?.disclosedContracts?.map((disclosedContract) => ({
        ...disclosedContract,
        domainId: domainId || meta?.domainId || '',
        synchronizerId: domainId || meta?.domainId || '',
      })) || []
    );
  }

  private getDeduplicationDuration() {
    return {
      DeduplicationDuration: {
        value: {
          seconds: this.deduplicationDurationInSeconds,
          nanos: 0,
        },
      },
    };
  }

  private static getTransactionFormat(
    parties: string[],
    transactionShape: TransactionFormatTransactionShapeEnum,
  ) {
    const filtersByParties = parties.reduce(
      (acc, party) => ({
        ...acc,
        [party]: {
          cumulative: [
            {
              identifierFilter: {
                WildcardFilter: {
                  value: {
                    includeCreatedEventBlob: true,
                  },
                },
              },
            },
          ],
        },
      }),
      {},
    );

    return {
      eventFormat: {
        filtersByParty: filtersByParties,
        verbose: false,
      },
      transactionShape,
    };
  }
}
